import { Controller } from "stimulus";
import { stockChartOptions } from "../../config";
import { getActiveCurrency, formatCurrency } from "../../util/currency";
import { EventCurrencyChanged } from "../../events";

const SUPPORTED_CURRENCIES_MAP = {
  usd: true,
  btc: true,
  eth: true
};

export default class extends Controller {
  static targets = ["container", "toolbar", "duration", "spinner"];

  connect() {
    this.dataSource = this.containerTarget.getAttribute("data-data-source");
    const activeTab = this.toolbarTarget.querySelector(".active");
    this.apiUrl = activeTab.getAttribute("data-url");
    if (!this.apiUrl) {
      throw new Error("Could not retrieve exchange volume url");
    }

    this.renderChart(this.apiUrl, this._chartCurrency);

    window.addEventListener(EventCurrencyChanged, e => {
      this.renderChart(this.apiUrl, this._chartCurrency);
    });
  }

  // Resolves to a currency that the chart supports, taking into consideration user currency.
  get _chartCurrency() {
    let vsCurrency = getActiveCurrency();

    if (!SUPPORTED_CURRENCIES_MAP[vsCurrency]) {
      // fallback to usd
      vsCurrency = "usd";
    }

    return vsCurrency;
  }

  updateChart(e) {
    // Remove active from siblings
    Array.from(e.target.parentElement.children).forEach(e => {
      e.classList.remove("active");
    });

    // Add active class to current
    e.target.classList.add("active");

    this.durationTarget.textContent = `(${e.target.innerText})`;
    this.spinnerTarget.style.display = "";

    this.apiUrl = e.target.getAttribute("data-url");
    this.renderChart(this.apiUrl, this._chartCurrency);
  }

  getVolumeFromResponse(json) {
    if (this.dataSource == "normalized_vol") {
      return json.normalized_volumes;
    } else if (this.dataSource == "reported_vol") {
      return json.volumes;
    } else if (this.dataSource == "open_interest") {
      return json.open_interest;
    }
  }

  renderChart(apiUrl, vsCurrency) {
    // todo(jon): ugly hack to use the correct currency
    apiUrl = apiUrl.replace("usd", vsCurrency);

    Promise.all([
      import("highcharts/highstock"),
      fetch(apiUrl, { credentials: "same-origin" }) // send cookie credentials
    ]).then(res => {
      const Highcharts = res[0];
      const response = res[1];

      Highcharts.setOptions({
        lang: {
          // Hide Zoom text
          rangeSelectorZoom: ''
        }
      });

      response.json().then(json => {
        const chart = Highcharts.StockChart(
          Object.assign(
            {
              title: "Exchange Trade Volume",
              yAxis: [
                {
                  showLastLabel: true,
                  opposite: false,
                  labels: {
                    style: {
                      color: "#56b4e9"
                    },
                    align: "left",
                    x: 0,
                    formatter() {
                      return formatCurrency(this.value, vsCurrency);
                    }
                  },
                  height: "100%"
                }
              ],
              tooltip: {
                formatter() {
                  let formatter =
                    `<b>${chart.time.dateFormat("%a %d %b, %H:%M:%S", this.x)}</b><br/>` +
                    "<b>Trade Volume: </b>" +
                    formatCurrency(this.points[0].y, vsCurrency);
                  return formatter;
                }
              },
              series: [
                {
                  name: "Exchange Volume",
                  type: "line",
                  data: this.getVolumeFromResponse(json).map(datum => {
                    // Convert string decimal number to number. Explore: do it in server level
                    datum[1] = Number(datum[1]);
                    return datum;
                  }),
                  marker: {
                    enabled: false
                  },
                  dataGrouping: {
                    enabled: false
                  }
                }
              ]
            },
            stockChartOptions(this.containerTarget.id)
          )
        );

        this.spinnerTarget.style.display = "none";
      });
    });
  }
}
